import type {
  CheckoutLine,
  SupplierOrderBatch,
  CatalogueEntry,
  CreateSupplierOrder,
  ReceiveSupplierOrder,
  ReorderSuggestion,
  SaveSupplier,
  SaveSupplierPrice,
  Supplier,
  SupplierImportPreview,
  SupplierImportRequest,
  SupplierImportResult,
  SupplierOrder,
  SupplierPrice,
  SaveSupplyCartLine,
  SupplyCartLine,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Suppliers, their prices and orders — STORY-171.
 *
 * Suppliers and prices are **the clinic's** (SU-D2); orders are the active
 * location's. Every call still carries the branch header, because a new item
 * is created at the location you are working in.
 *
 * Who may do what (SU-D8): reading is part of seeing inventory; suppliers and
 * prices are for whoever sets stock up; ordering and receiving are for whoever
 * records a movement. The server refuses anyone else.
 */

export function listSuppliers(signal?: AbortSignal): Promise<Supplier[]> {
  return api<Supplier[]>("/suppliers", { branchScoped: true, signal });
}

export function createSupplier(body: SaveSupplier): Promise<Supplier> {
  return api<Supplier>("/suppliers", { method: "POST", body, branchScoped: true });
}

export function updateSupplier(supplierId: string, body: Partial<SaveSupplier>): Promise<Supplier> {
  return api<Supplier>(`/suppliers/${supplierId}`, { method: "PATCH", body, branchScoped: true });
}

export function addSupplierPrice(
  supplierId: string,
  body: SaveSupplierPrice,
): Promise<SupplierPrice> {
  return api<SupplierPrice>(`/suppliers/${supplierId}/prices`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

export function updateSupplierPrice(
  priceId: string,
  body: Partial<Omit<SaveSupplierPrice, "itemName" | "createItem">>,
): Promise<SupplierPrice> {
  return api<SupplierPrice>(`/supplier-prices/${priceId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

export function removeSupplierPrice(priceId: string): Promise<void> {
  return api<void>(`/supplier-prices/${priceId}`, { method: "DELETE", branchScoped: true });
}

/** Changes nothing — the server plans the import and says what each row would do. */
export function previewSupplierImport(
  supplierId: string,
  body: SupplierImportRequest,
): Promise<SupplierImportPreview> {
  return api<SupplierImportPreview>(`/suppliers/${supplierId}/import/preview`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

export function runSupplierImport(
  supplierId: string,
  body: SupplierImportRequest,
): Promise<SupplierImportResult> {
  return api<SupplierImportResult>(`/suppliers/${supplierId}/import`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/** Everything the clinic's own suppliers sell, one row per item (STORY-189b). */
export function listCatalogue(signal?: AbortSignal): Promise<CatalogueEntry[]> {
  return api<CatalogueEntry[]>("/suppliers/catalogue", { branchScoped: true, signal });
}

/** Put a catalogue item on this location's shelf from one price line. Idempotent. */
export function addCatalogueToStock(
  priceId: string,
  /** In this location's currency — asked for only where it differs from the clinic's (IA-D9). */
  priceCents?: number,
): Promise<{ itemId: string; created: boolean }> {
  return api<{ itemId: string; created: boolean }>("/suppliers/catalogue/add-to-stock", {
    method: "POST",
    body: priceCents === undefined ? { priceId } : { priceId, priceCents },
    branchScoped: true,
  });
}

/** What is low at this location, and who to buy it from (SU-D3). */
export function listReorder(signal?: AbortSignal): Promise<ReorderSuggestion[]> {
  return api<ReorderSuggestion[]>("/suppliers/reorder", { branchScoped: true, signal });
}

/** Check out a cart: one order per supplier, all or nothing (STORY-189c). */
export function checkoutCart(lines: CheckoutLine[]): Promise<SupplierOrderBatch[]> {
  return api<SupplierOrderBatch[]>("/supplier-orders/checkout", {
    method: "POST",
    body: { lines },
    branchScoped: true,
  });
}

export function listSupplierOrders(signal?: AbortSignal): Promise<SupplierOrder[]> {
  return api<SupplierOrder[]>("/supplier-orders", { branchScoped: true, signal });
}

export function createSupplierOrder(body: CreateSupplierOrder): Promise<SupplierOrder> {
  return api<SupplierOrder>("/supplier-orders", { method: "POST", body, branchScoped: true });
}

export function receiveSupplierOrder(
  orderId: string,
  body: ReceiveSupplierOrder,
): Promise<SupplierOrder> {
  return api<SupplierOrder>(`/supplier-orders/${orderId}/receive`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/** Nothing is coming from this checkout: cancels every line still open (STORY-189c). */
export function cancelSupplierBatch(batchId: string): Promise<SupplierOrder[]> {
  return api<SupplierOrder[]>(`/supplier-orders/batches/${batchId}/cancel`, {
    method: "POST",
    branchScoped: true,
  });
}

export function cancelSupplierOrder(orderId: string): Promise<SupplierOrder> {
  return api<SupplierOrder>(`/supplier-orders/${orderId}/cancel`, {
    method: "POST",
    branchScoped: true,
  });
}

/**
 * The location's cart, shared by everyone ordering there (SU-D17). Every write
 * answers with the whole cart, so a colleague's lines arrive with your own.
 */
export function listCart(signal?: AbortSignal): Promise<SupplyCartLine[]> {
  return api<SupplyCartLine[]>("/supply-cart", { branchScoped: true, signal });
}

export function saveCartLine(
  inventoryItemId: string,
  body: SaveSupplyCartLine,
): Promise<SupplyCartLine[]> {
  return api<SupplyCartLine[]>(`/supply-cart/${inventoryItemId}`, {
    method: "PUT",
    body,
    branchScoped: true,
  });
}

export function removeCartLine(inventoryItemId: string): Promise<void> {
  return api<void>(`/supply-cart/${inventoryItemId}`, { method: "DELETE", branchScoped: true });
}

export function clearCart(): Promise<void> {
  return api<void>("/supply-cart", { method: "DELETE", branchScoped: true });
}

/** The supplier was messaged (SU-D18): marks these open lines sent, now, by the caller. */
export function markOrdersSent(orderIds: string[]): Promise<SupplierOrder[]> {
  return api<SupplierOrder[]>("/supplier-orders/sent", {
    method: "POST",
    body: { orderIds },
    branchScoped: true,
  });
}
